import type { LucideIcon } from 'lucide-react';
import {
    SitePerspectivePhase,
    SitePerspectiveType,
    SitePerspectiveTypeI18N,
    developmentProcess,
    existingAssetAcquisitionProcess,
    landDevelopmentProcess,
    residentialPurchaseProcess,
} from '@the-real-insight/tri-model';
import {
    Banknote,
    Building2,
    Calculator,
    CalendarCheck,
    ClipboardList,
    Cpu,
    DoorOpen,
    FileCheck2,
    FileText,
    Gavel,
    Globe,
    KeyRound,
    Landmark,
    Lightbulb,
    MapPin,
    Scale,
    ShieldCheck,
    TrendingUp,
    TriangleAlert,
    Users,
    Wrench,
} from 'lucide-react';

/**
 * The portfolio dashboard's process model.
 *
 * A property is looked at through a **process type** — new build, acquisition or
 * sale — and each type runs through three steps. The later steps are the same
 * everywhere; what differs is how the property is judged up front: a new build
 * argues an investment case, an acquisition values what already stands.
 *
 * Only structure and presentation live here. The perspectives have no content
 * yet; the tiles are the anchors the content will later be hung from.
 */

export type ProcessTypeKey =
    | 'landDevelopment'
    | 'existingAssetAcquisition'
    | 'newBuild'
    | 'acquisition'
    | 'sale'
    | 'development'
    | 'residentialPurchase';

/**
 * Die Schritte der drei alten Typen stehen hier, die der Projektentwicklung im Modell.
 *
 * Deshalb die Vereinigung: Ein Schritt der Projektentwicklung *ist* eine Phase des Modells, und ihn
 * hier ein zweites Mal zu benennen hieße, zwei Namen für dieselbe Sache zu führen — genau das, was
 * beim Attributkatalog schon einmal vermieden wurde.
 */
export type ProcessStepKey =
    | 'buildingRightCreation'
    | 'investmentCase'
    | 'valuation'
    | 'transaction'
    | 'operation'
    | SitePerspectivePhase;


export interface Perspective {
    key: string;
    /** i18n key under `portfolioDashboard.perspectives.<step>.` */
    labelKey: string;
    icon: LucideIcon;
    /**
     * Set where the perspective is backed by a `SitePerspective`. Then its title and its attributes
     * come from the model rather than from the portal, so the two cannot drift apart.
     */
    perspectiveType?: SitePerspectiveType;
}

export interface ProcessStep {
    key: ProcessStepKey;
    /** i18n key under `portfolioDashboard.steps.` — leer, wo das Modell die Beschriftung trägt. */
    labelKey: string;
    /**
     * Die Beschriftung aus dem Modell, wo es eine gibt.
     *
     * Dieselbe Bauweise wie bei `Perspective.perspectiveType`: Wo das Modell etwas benennt, gilt
     * seine Benennung, und die Sprachdateien tragen es nicht ein zweites Mal. „Public Law /
     * Environmental DD" ist der Name, unter dem die Fachseite die Phase kennt.
     */
    label?: { de: string; en: string };
    perspectives: Perspective[];
}

export interface ProcessType {
    key: ProcessTypeKey;
    /** i18n key under `portfolioDashboard.types.` */
    labelKey: string;
    steps: ProcessStep[];
}

const perspective = (step: ProcessStepKey, key: string, icon: LucideIcon): Perspective => ({
    key,
    labelKey: `portfolioDashboard.perspectives.${step}.${key}`,
    icon,
});

/**
 * First step of a new build: working out, prioritising and steering the creation of building right.
 * Its six perspectives are the `SitePerspective` types — the model owns their titles and fields.
 */
const buildingRightCreationStep: ProcessStep = {
    key: 'buildingRightCreation',
    labelKey: 'portfolioDashboard.steps.buildingRightCreation',
    perspectives: [
        { key: SitePerspectiveType.cataster, labelKey: '', icon: Scale, perspectiveType: SitePerspectiveType.cataster },
        { key: SitePerspectiveType.zoning, labelKey: '', icon: FileCheck2, perspectiveType: SitePerspectiveType.zoning },
        {
            key: SitePerspectiveType.marketAndLocation,
            labelKey: '',
            icon: TrendingUp,
            perspectiveType: SitePerspectiveType.marketAndLocation,
        },
        {
            key: SitePerspectiveType.areaPotential,
            labelKey: '',
            icon: Building2,
            perspectiveType: SitePerspectiveType.areaPotential,
        },
        {
            key: SitePerspectiveType.underwriting,
            labelKey: '',
            icon: Calculator,
            perspectiveType: SitePerspectiveType.underwriting,
        },
        {
            key: SitePerspectiveType.projectStatus,
            labelKey: '',
            icon: ClipboardList,
            perspectiveType: SitePerspectiveType.projectStatus,
        },
    ],
};

/** First step of a new build or a sale: the case being made for the property. */
const investmentCaseStep: ProcessStep = {
    key: 'investmentCase',
    labelKey: 'portfolioDashboard.steps.investmentCase',
    perspectives: [
        perspective('investmentCase', 'location', MapPin),
        perspective('investmentCase', 'investmentThesis', Lightbulb),
        perspective('investmentCase', 'riskReturnProfile', TrendingUp),
        perspective('investmentCase', 'businessPlan', ClipboardList),
        perspective('investmentCase', 'financing', Landmark),
        perspective('investmentCase', 'exit', DoorOpen),
    ],
};

/** First step of an acquisition: valuing a property that already exists. */
const valuationStep: ProcessStep = {
    key: 'valuation',
    labelKey: 'portfolioDashboard.steps.valuation',
    perspectives: [
        perspective('valuation', 'microLocation', MapPin),
        perspective('valuation', 'macroLocation', Globe),
        perspective('valuation', 'letting', KeyRound),
        perspective('valuation', 'acquisitionCosts', Banknote),
        perspective('valuation', 'propertyManagement', Building2),
        perspective('valuation', 'financing', Landmark),
    ],
};

const transactionStep: ProcessStep = {
    key: 'transaction',
    labelKey: 'portfolioDashboard.steps.transaction',
    perspectives: [
        perspective('transaction', 'legalPropertyDocuments', FileText),
        perspective('transaction', 'buildingLawAndPermits', FileCheck2),
        perspective('transaction', 'leases', KeyRound),
        perspective('transaction', 'economicDocuments', Calculator),
        perspective('transaction', 'administrationAndCompliance', ShieldCheck),
        perspective('transaction', 'technicalDocuments', Wrench),
    ],
};

const operationStep: ProcessStep = {
    key: 'operation',
    labelKey: 'portfolioDashboard.steps.operation',
    perspectives: [
        perspective('operation', 'tenants', Users),
        perspective('operation', 'propertyManagement', Building2),
        perspective('operation', 'technicalInstallations', Cpu),
        perspective('operation', 'maintenanceAndInspection', CalendarCheck),
        perspective('operation', 'damages', TriangleAlert),
        perspective('operation', 'legalMatters', Gavel),
    ],
};

/**
 * Ein Symbol je Perspektive der Projektentwicklung.
 *
 * Das Einzige, was die Oberfläche zu diesen Perspektiven beizutragen hat: Ihre Bezeichnung und ihr
 * Platz im Verlauf stehen im Modell, ein Symbol hat dort nichts zu suchen.
 */
const MODEL_ICONS: Record<string, LucideIcon> = {
    [SitePerspectiveType.dealAndGovernance]: Users,
    [SitePerspectiveType.legalAndTitle]: Scale,
    [SitePerspectiveType.planningAndPermits]: FileCheck2,
    [SitePerspectiveType.designConstructionAndEsg]: Wrench,
    [SitePerspectiveType.commercialAndMarket]: TrendingUp,
    [SitePerspectiveType.financeFundingAndTax]: Banknote,
    // Der Ankauf eines Wohngebäudes bringt drei eigene mit; „Markt und Standort" teilt er mit dem
    // Neubau und damit auch dessen Symbol.
    [SitePerspectiveType.commercialAndOperations]: KeyRound,
    [SitePerspectiveType.financeAndTax]: Banknote,
    [SitePerspectiveType.technicalAndEsg]: Cpu,
    [SitePerspectiveType.marketAndLocation]: TrendingUp,
    // Die Grundstücksentwicklung nutzt die sechs Perspektiven der Projektentwicklung (oben).

    // Bestandsankauf.
    [SitePerspectiveType.microLocationBenchmark]: MapPin,
    [SitePerspectiveType.rentalPotential]: TrendingUp,
    [SitePerspectiveType.propertyKeyData]: Building2,
    [SitePerspectiveType.unitMix]: DoorOpen,
    [SitePerspectiveType.acquisitionReturns]: Banknote,
    [SitePerspectiveType.incomeAndVacancy]: KeyRound,
    [SitePerspectiveType.exitAndFinancing]: Landmark,
    [SitePerspectiveType.returnDerivation]: Calculator,
    [SitePerspectiveType.lettingActivity]: Users,
    [SitePerspectiveType.technicalFindings]: Wrench,
    [SitePerspectiveType.leasesAndAuthorities]: FileCheck2,
    [SitePerspectiveType.taxAndOtherRisks]: TriangleAlert,
    [SitePerspectiveType.purchaseContractMeasures]: Gavel,
};

/**
 * Die Schritte eines Prozesstyps, dessen Verlauf im Modell steht.
 *
 * Einmal geschrieben für beide: Projektentwicklung und Ankauf unterscheiden sich in ihren Phasen,
 * nicht darin, wie ein Verlauf zu einer Leiste wird.
 */
const stepsFromModel = (process: typeof developmentProcess): ProcessStep[] =>
    process.map((phase) => ({
        key: phase.phase,
        labelKey: '',
        label: phase.label,
        // `requirement.type` und nicht der Eintrag selbst: Seit die Perspektive im Modell ihre
        // Dokumente mitträgt, ist sie dort ein Objekt und kein bloßer Bezeichner mehr.
        perspectives: phase.perspectives.map((requirement) => ({
            key: requirement.type,
            labelKey: '',
            icon: MODEL_ICONS[requirement.type] ?? ClipboardList,
            perspectiveType: requirement.type,
        })),
    }));

/**
 * Die Schritte der Projektentwicklung, aus dem Modell gelesen.
 *
 * Dreißig Phasen, und dieselbe Perspektive taucht in mehreren auf — „Commercial & Market" in vier,
 * „Design, Construction & ESG" in acht. Das ist kein Fehler der Vorlage, sondern ihr Bauprinzip:
 * Dieselbe Frage stellt sich in der Ansprache anders als in der Ausführung.
 *
 * Abgeleitet und nicht abgeschrieben: Wer die Zuordnung ändern will, ändert sie im Modell, und
 * Server und Oberfläche folgen. Eine zweite Liste hier wäre die Sorte Kopie, die still altert.
 */
const developmentSteps: ProcessStep[] = stepsFromModel(developmentProcess);

/** Und derselbe Weg für den Ankauf eines Wohngebäudes: sechsundzwanzig Phasen aus dem Modell. */
const residentialPurchaseSteps: ProcessStep[] = stepsFromModel(residentialPurchaseProcess);

/** Grundstücksentwicklung (acht Phasen) und Bestandsankauf (drei) — ebenfalls aus dem Modell. */
const landDevelopmentSteps: ProcessStep[] = stepsFromModel(landDevelopmentProcess);
const existingAssetAcquisitionSteps: ProcessStep[] = stepsFromModel(existingAssetAcquisitionProcess);

export const processTypes: ProcessType[] = [
    {
        key: 'landDevelopment',
        labelKey: 'portfolioDashboard.types.landDevelopment',
        steps: landDevelopmentSteps,
    },
    {
        key: 'existingAssetAcquisition',
        labelKey: 'portfolioDashboard.types.existingAssetAcquisition',
        steps: existingAssetAcquisitionSteps,
    },
    {
        key: 'newBuild',
        labelKey: 'portfolioDashboard.types.newBuild',
        steps: [buildingRightCreationStep, investmentCaseStep, transactionStep, operationStep],
    },
    {
        key: 'acquisition',
        labelKey: 'portfolioDashboard.types.acquisition',
        steps: [valuationStep, transactionStep, operationStep],
    },
    {
        key: 'sale',
        labelKey: 'portfolioDashboard.types.sale',
        steps: [investmentCaseStep, transactionStep, operationStep],
    },
    {
        key: 'development',
        labelKey: 'portfolioDashboard.types.development',
        steps: developmentSteps,
    },
    {
        key: 'residentialPurchase',
        labelKey: 'portfolioDashboard.types.residentialPurchase',
        steps: residentialPurchaseSteps,
    },
];

/**
 * The type the dashboard opens on. Acquisition rather than the first entry of the
 * list: it is the case the perspectives are worked through in most detail today.
 */
export const defaultTypeKey: ProcessTypeKey = 'acquisition';

/** The perspective's heading — from the model where it is backed by one, else from the locale. */
export const perspectiveTitle = (
    perspective: Perspective,
    translate: (key: string) => string,
    language: string,
): string => {
    if (perspective.perspectiveType) {
        const label = SitePerspectiveTypeI18N[perspective.perspectiveType];
        return language.startsWith('en') ? label.en : label.de;
    }
    return translate(perspective.labelKey);
};

/**
 * Die Überschrift eines Schrittes — aus dem Modell, wo es eine trägt, sonst aus den Sprachdateien.
 *
 * Dasselbe Muster wie `perspectiveTitle` darüber, und aus demselben Grund: Was das Modell benennt,
 * soll die Oberfläche nicht ein zweites Mal benennen.
 */
export const stepTitle = (
    step: ProcessStep,
    translate: (key: string) => string,
    language: string,
): string => (step.label ? (language.startsWith('en') ? step.label.en : step.label.de) : translate(step.labelKey));

export const findType = (key: string | null | undefined): ProcessType =>
    processTypes.find((type) => type.key === key) ??
    processTypes.find((type) => type.key === defaultTypeKey) ??
    processTypes[0];

/** The named step of the type, or its first step when the name does not belong to it. */
export const findStep = (type: ProcessType, key: string | null | undefined): ProcessStep =>
    type.steps.find((step) => step.key === key) ?? type.steps[0];

/**
 * A step's place in its type — and with it its shade on the rail.
 *
 * Derived from the position rather than stored on the step: a step appears in more than one type
 * and not always at the same place, and a stored shade meant a new-build's first two steps came out
 * the same colour the moment a step was added in front.
 */
export const stepIndex = (type: ProcessType, step: ProcessStep): number =>
    Math.max(0, type.steps.findIndex((entry) => entry.key === step.key));
